import WebPushService from '../utils/WebPushService';
import { Capacitor } from '@capacitor/core';
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PhotoAvatar from "../components/photos/PhotoAvatar";
import { 
  Target, Users, ChevronLeft, Sun, Moon, Star, User, BellRing, Sparkles, X, Send, RotateCcw,
  TrendingUp, ClipboardList, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useSwipeToClose } from "../hooks/useSwipeToClose";
import { useUser } from "../context/UserContext";
import { API, getGreeting } from "../utils/constants";
import { WidgetCard, CircularProgress } from "../components";
import NotificationService from "../utils/NotificationService";
import { MessageCircle } from "lucide-react";
import NotificationBell from "../components/layout/NotificationBell";
import SessionsDesignerWidget from '../components/planner/SessionsDesignerWidget';

export const HomePage = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, isCoach } = useUser();
  const [userName, setUserName] = useState("User");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ sessions: 0, maxSessions: 20, goalsAchieved: 0, goalsInProgress: 0, streak: 0, avgRating: 0 });
  const [lastSession, setLastSession] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [currentSponsorIndex, setCurrentSponsorIndex] = useState(0);
  const [sponsorsData, setSponsorsData] = useState([]);
  const [teamCount, setTeamCount] = useState(0);
  const [teamGoals, setTeamGoals] = useState([]);
  const [unratedCount, setUnratedCount] = useState(0);
  const [showAiCoachModal, setShowAiCoachModal] = useState(false);
  const [aiChatInput, setAiChatInput] = useState("");
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [aiChatSessionId, setAiChatSessionId] = useState("");
  const aiMessagesEndRef = useRef(null);
  const aiModalSwipe = useSwipeToClose(() => setShowAiCoachModal(false));

  const sponsors = sponsorsData.length > 0 ? sponsorsData : [
    { name: 'Nike', logo_url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Logo_NIKE.svg/1200px-Logo_NIKE.svg.png', link_url: 'https://www.nike.com' },
    { name: 'Adidas', logo_url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/20/Adidas_Logo.svg/1200px-Adidas_Logo.svg.png', link_url: 'https://www.adidas.com' },
    { name: 'Puma', logo_url: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/da/Puma_complete_logo.svg/1200px-Puma_complete_logo.svg.png', link_url: 'https://www.puma.com' }
  ];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const userId = user?.id;
        const res = await axios.get(`${API}/partners${userId ? `?user_id=${userId}` : ""}`);
        if (mounted && Array.isArray(res.data?.items)) {
          setSponsorsData(res.data.items);
          setCurrentSponsorIndex(0);
        }
      } catch {
        /* fallback to default sponsors */
      }
    })();
    return () => { mounted = false; };
  }, [user?.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSponsorIndex((prev) => (prev + 1) % sponsors.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [sponsors.length]);

  useEffect(() => {
    if (user?.account_type !== 'athlete') return undefined;
    let active = true;
    let timeout;
    const check = async () => {
      const native = Capacitor.getPlatform() === 'android';
      if (native ? !NotificationService.isSupported() : !WebPushService.isSupported()) return;
      try {
        const enabled = native ? NotificationService.getPermission() === 'granted' : await WebPushService.isEnabled(user.id);
        if (!active) return;
        setNotificationsEnabled(enabled);
        const hasAsked = localStorage.getItem(`mygoal-reminder-prompt-v2:${user.id}`);
        if (!enabled && !hasAsked && NotificationService.getPermission() !== 'denied') {
          timeout = setTimeout(() => setShowNotificationPrompt(true), 2000);
        }
      } catch (_) { /* Profile offers an explicit retry if the server is offline. */ }
    };
    check();
    return () => { active = false; clearTimeout(timeout); };
  }, [user?.id, user?.account_type]);

  const handleEnableNotifications = async () => {
    let granted = false;
    try {
      if (user?.account_type === 'athlete' && Capacitor.getPlatform() !== 'android') {
        await WebPushService.enable(user.id);
        granted = true;
      } else {
        granted = await NotificationService.requestPermission();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Could not enable reminders. Please retry in your profile.');
      return;
    }
    setNotificationsEnabled(granted);
    setShowNotificationPrompt(false);
    localStorage.setItem(`mygoal-reminder-prompt-v2:${user?.id}`, 'true');
    if (granted) {
      toast.success("Notifications enabled!");
      window.dispatchEvent(new Event('mygoal-notifications-enabled'));
    } else {
      toast.info("Notifications disabled.");
    }
  };

  const handleDismissNotificationPrompt = () => {
    setShowNotificationPrompt(false);
    localStorage.setItem(`mygoal-reminder-prompt-v2:${user?.id}`, 'true');
  };

  const createAiSessionId = () => `coach-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const getAiSessionStorageKey = () => `mygoal-ai-sparring-session-${user?.id || "unknown"}`;

  const loadAiSparringMessages = async (sessionId) => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/coach/ai-sparring?coach_id=${user.id}&session_id=${sessionId}`);
      setAiChatMessages(response.data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load AI sparring session");
      setAiChatMessages([]);
    }
  };

  const startNewAiSparringSession = () => {
    if (!user?.id) return;
    const newSessionId = createAiSessionId();
    localStorage.setItem(getAiSessionStorageKey(), newSessionId);
    setAiChatSessionId(newSessionId);
    setAiChatMessages([]);
    setAiChatInput("");
  };

  const handleSendAiSparringMessage = async () => {
    if (!user?.id || aiChatLoading) return;

    const messageText = aiChatInput.trim();
    if (!messageText) return;

    const activeSessionId = aiChatSessionId || createAiSessionId();
    if (!aiChatSessionId) {
      localStorage.setItem(getAiSessionStorageKey(), activeSessionId);
      setAiChatSessionId(activeSessionId);
    }

    const optimisticMessage = { role: "user", text: messageText, created_at: new Date().toISOString() };
    setAiChatMessages((prev) => [...prev, optimisticMessage]);
    setAiChatInput("");
    setAiChatLoading(true);

    try {
      const response = await axios.post(`${API}/coach/ai-sparring`, {
        coach_id: user.id,
        session_id: activeSessionId,
        message: messageText,
      });
      setAiChatMessages(response.data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send sparring message");
    } finally {
      setAiChatLoading(false);
    }
  };

  useEffect(() => {
    if (!showAiCoachModal || !user?.id) return;

    const storageKey = getAiSessionStorageKey();
    const storedSessionId = localStorage.getItem(storageKey);
    const activeSessionId = storedSessionId || createAiSessionId();

    if (!storedSessionId) {
      localStorage.setItem(storageKey, activeSessionId);
    }

    setAiChatSessionId(activeSessionId);
    loadAiSparringMessages(activeSessionId);
  }, [showAiCoachModal, user?.id]);

  useEffect(() => {
    aiMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiChatMessages, aiChatLoading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = user?.id;
        if (!userId) return;
        const profileUrl = `${API}/profile?user_id=${userId}`;
        const statsUrl = `${API}/stats/overview?user_id=${userId}`;
        const [profileRes, statsRes] = await Promise.all([
          axios.get(profileUrl),
          axios.get(statsUrl)
        ]);
        setUserName(profileRes.data.name);
        setStats({
          sessions: statsRes.data.this_month_sessions,
          maxSessions: 20,
          goalsAchieved: statsRes.data.goals_achieved,
          goalsInProgress: statsRes.data.goals_in_progress,
          streak: statsRes.data.current_streak,
          avgRating: statsRes.data.average_goal_rating || 0
        });
        try {
          const coach = user.account_type === 'coach';
          const lastRes = await axios.get(coach ? `${API}/coach/team/last-training?coach_id=${userId}` : `${API}/sessions/last?user_id=${userId}`);
          setLastSession(coach ? lastRes.data.last_session : lastRes.data);
        } catch (e) {
          // No last session is expected for new users — silently continue
        }
        if (user.account_type === 'athlete') {
          try {
            const sessionsRes = await axios.get(`${API}/sessions?user_id=${userId}`);
            setUnratedCount(sessionsRes.data.filter(s => !s.rating).length);
          } catch (e) {
            console.warn('Failed to fetch sessions for unrated count:', e);
          }
        }
        if (profileRes.data.account_type === 'coach') {
          try {
            const teamRes = await axios.get(`${API}/coach/team?user_id=${userId}`);
            setTeamCount(teamRes.data.length);
            const goalsPromises = teamRes.data.map(async (athlete) => {
              try {
                const goalsRes = await axios.get(`${API}/athlete/${athlete.id}/goals`);
                return { athlete_id: athlete.id, athlete_name: athlete.name, goals: goalsRes.data };
              } catch { return { athlete_id: athlete.id, athlete_name: athlete.name, goals: [] }; }
            });
            const allTeamGoals = await Promise.all(goalsPromises);
            setTeamGoals(allTeamGoals.filter(a => a.goals.length > 0));
          } catch (e) {
            console.warn('Failed to fetch coach team data:', e);
          }
        }
      } catch (e) {
        console.error("Error fetching data:", e);
        setUserName("Alex");
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      setUserName(user.name);
      if (user.account_type === 'coach') {
        const fetchTeamData = async () => {
          try {
            const teamRes = await axios.get(`${API}/coach/team?user_id=${user.id}`);
            setTeamCount(teamRes.data.length);
            const goalsPromises = teamRes.data.map(async (athlete) => {
              try {
                const goalsRes = await axios.get(`${API}/athlete/${athlete.id}/goals`);
                return { athlete_id: athlete.id, athlete_name: athlete.name, goals: goalsRes.data };
              } catch { return { athlete_id: athlete.id, athlete_name: athlete.name, goals: [] }; }
            });
            const allTeamGoals = await Promise.all(goalsPromises);
            setTeamGoals(allTeamGoals.filter(a => a.goals.length > 0));
          } catch (e) {
            console.warn('Failed to fetch team goals:', e);
          }
        };
        fetchTeamData();
      }
    }
    fetchData();
  }, [user]);

  const lastActivityTimestamp = lastSession?.completed_at;
  const lastActivityDate = lastActivityTimestamp ? new Date(lastActivityTimestamp) : null;
  const lastActivityWhen = lastActivityDate && Number.isFinite(lastActivityDate.getTime())
    ? lastActivityDate.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col p-4 pb-8 transition-colors duration-300`} data-testid="home-page">
      {showNotificationPrompt && (
        <div className={`fixed top-4 left-4 right-4 z-50 p-4 rounded-2xl ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'} shadow-xl border ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#493986] to-[#92a7e8] flex items-center justify-center flex-shrink-0">
              <BellRing className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${theme.textPrimary}`}>Enable Notifications?</h3>
              <p className={`text-sm ${theme.textMuted} mt-1`}>Choose your goal at 09:00 on training days and rate your activity 30 minutes after it ends.</p>
              <div className="flex gap-2 mt-3">
                <button onClick={handleEnableNotifications} className="px-4 py-2 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white text-sm font-medium rounded-xl">Enable</button>
                <button onClick={handleDismissNotificationPrompt} className={`px-4 py-2 ${theme.button} ${theme.textSecondary} text-sm rounded-xl`}>Not now</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="flex items-center justify-between mb-6">
        <button data-testid="theme-toggle" onClick={toggleTheme} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          {isDark ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-[#493986]" />}
        </button>
        <h1 className={`text-lg font-semibold ${theme.textPrimary}`} data-testid="greeting-section">
          {loading ? "..." : `${getGreeting()}, ${userName}`}
        </h1>
        <div className="flex items-center gap-2">
          {['athlete', 'coach'].includes(user?.account_type) && <button aria-label="Open private chat" onClick={() => navigate('/chat')} className={`w-10 h-10 rounded-full flex items-center justify-center ${theme.button}`}><MessageCircle className={`w-5 h-5 ${theme.textPrimary}`} /></button>}
          <NotificationBell />
          <button aria-label="Open profile" data-testid="profile-button" onClick={() => navigate("/profile")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
            <PhotoAvatar photoUrl={user?.photo_url} name={user?.name} className="h-10 w-10 rounded-full" fallback={<User className="h-5 w-5" />} />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-4 flex-1">
        <WidgetCard testId="widget-agenda" onClick={() => navigate("/agenda")} isDark={isDark} className="col-span-2 h-[82px] p-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-4xl font-bold"><span className="bg-gradient-to-r from-[#493986] to-[#92a7e8] bg-clip-text text-transparent">{new Date().getDate()}</span></div>
            <div><p className={`font-semibold ${theme.textPrimary}`}>Agenda</p><p className={`text-xs ${theme.textMuted}`}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short' })}</p></div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-full ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}><span className={`text-xs font-medium ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>{unratedCount > 0 ? `${unratedCount} to rate` : 'All rated'}</span></div>
            <ChevronLeft className={`w-5 h-5 rotate-180 ${theme.textMuted}`} />
          </div>
        </WidgetCard>

        {user?.club_id && <WidgetCard testId="widget-club-exercises" onClick={() => navigate("/club-exercises")} isDark={isDark} className="col-span-2 p-5 flex items-center justify-between"><div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-violet-500/15"><ClipboardList size={26} className="text-violet-400" /></div><div><h3 className={`font-semibold ${theme.textPrimary}`}>Club exercise library</h3><p className={`text-sm mt-1 ${theme.textSecondary}`}>Explore exercises shared within your club</p></div></div><ChevronRight size={20} className={theme.textSecondary} /></WidgetCard>}

        {isCoach && <SessionsDesignerWidget isDark={isDark} />}

        <WidgetCard testId="widget-sessions" onClick={() => navigate(isCoach ? "/team/sessions" : "/sessions")} isDark={isDark} className="col-span-1 p-6 flex flex-col items-center justify-center min-h-[180px]">
          <CircularProgress value={stats.sessions} max={stats.maxSessions} isDark={isDark} />
          <p className={`font-semibold mt-3 ${theme.textPrimary}`}>{isCoach ? 'Team Sessions' : 'Sessions'}</p>
          <p className={`text-xs ${theme.textMuted}`}>This Month</p>
        </WidgetCard>

        <WidgetCard testId="widget-last-training" onClick={() => navigate("/last-training")} isDark={isDark} className="col-span-1 p-6 flex flex-col items-center justify-center min-h-[180px]">
          {!isCoach && lastSession && !lastSession.rating && (
            <span
              data-testid="last-training-new-badge"
              className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide text-white bg-gradient-to-r from-red-500 to-orange-500 shadow-lg shadow-orange-500/30 animate-pulse"
            >
              NEW
            </span>
          )}
          <div className="w-[120px] h-[120px] flex items-center justify-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center">
              <Star className={`w-10 h-10 ${isDark ? 'text-yellow-400' : 'text-[#493986]'}`} />
            </div>
          </div>
          <p className={`font-semibold mt-3 ${theme.textPrimary}`}>Last Activity</p>
          <em className={`mt-1 block text-[11px] leading-4 ${theme.textMuted} text-center`} data-testid="last-activity-details">
            {lastSession ? <>
              <span className="block line-clamp-2">{lastSession.name || lastSession.type || 'Activity'}</span>
              {lastActivityWhen ? <time className="block" dateTime={lastActivityTimestamp}>{lastActivityWhen}</time> : <span className="block">Date unavailable</span>}
            </> : 'No activity yet'}
          </em>
          {!isCoach && lastSession && <p className={`mt-1 text-xs ${theme.textMuted} text-center`}>{lastSession.rating ? 'Rated ✓' : 'Rate now!'}</p>}
        </WidgetCard>

        {isCoach ? (
          <WidgetCard testId="widget-mygoal" onClick={() => navigate("/team/goals")} gradient={true} isDark={isDark} className="col-span-1 row-span-2 p-5 flex flex-col items-center justify-center min-h-[200px]">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mb-4"><Target className="w-8 h-8 text-white" /></div>
            <h3 className="text-xl font-bold text-white mb-1">My Team Goals</h3>
            <p className="text-3xl font-bold text-white/90">{teamGoals.reduce((acc, a) => acc + a.goals.length, 0)}</p>
            <p className="text-white/60 text-sm">total goals</p>
          </WidgetCard>
        ) : (
          <WidgetCard testId="widget-mygoal" onClick={() => navigate("/mygoal")} gradient={true} isDark={isDark} className="col-span-1 row-span-2 p-5 flex flex-col justify-between min-h-[200px]">
            <div className="self-end"><span className="text-white/60 text-xs">{stats.goalsInProgress} active</span></div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1">Training Goals</h3>
              <p className="text-white/80 text-sm mb-3">Rate your progress</p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1"><Star className="w-6 h-6 text-yellow-400 fill-yellow-400" /><span className="text-2xl font-bold text-white">{stats.avgRating > 0 ? stats.avgRating.toFixed(1) : "-"}</span></div>
                <span className="text-white/60 text-sm">avg</span>
              </div>
              <p className="text-white/70 text-sm mt-2">{stats.goalsAchieved} achieved</p>
            </div>
            <button className="mt-4 flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 self-start hover:bg-white/30 transition-colors">
              <span className="text-white text-sm font-medium">My Goals</span>
              <ChevronLeft className="w-4 h-4 text-white rotate-180" />
            </button>
          </WidgetCard>
        )}

        {isCoach ? (
          <WidgetCard testId="widget-team" onClick={() => navigate("/team")} isDark={isDark} className="col-span-1 p-5 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center mb-3"><Users className={`w-8 h-8 ${isDark ? 'text-white' : 'text-[#493986]'}`} /></div>
            <p className={`font-semibold ${theme.textPrimary}`}>My Team</p>
            <p className={`text-xs ${theme.textMuted}`}>{teamCount} athlete{teamCount !== 1 ? 's' : ''}</p>
          </WidgetCard>
        ) : (
          <WidgetCard testId="widget-coaches" onClick={() => navigate("/coaches")} isDark={isDark} className="col-span-1 p-5 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center mb-3"><Users className={`w-8 h-8 ${isDark ? 'text-white' : 'text-[#493986]'}`} /></div>
            <p className={`font-semibold ${theme.textPrimary}`}>Coaches</p>
            <p className={`text-xs ${theme.textMuted}`}>Get Help</p>
          </WidgetCard>
        )}

        {isCoach ? (
          /* AI Training Advice Widget for Coaches */
          <WidgetCard 
            testId="widget-ai-advice" 
            onClick={() => navigate("/ai-advice")}
            isDark={isDark} 
            className="col-span-1 p-5 flex flex-col items-center justify-center relative overflow-hidden"
          >
            {/* Beta Badge */}
            <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              BETA
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className={`rounded-xl px-4 py-2 ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                <TrendingUp className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <div className={`rounded-xl px-4 py-2 ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'}`}>
                <Sparkles className={`w-6 h-6 ${isDark ? 'text-yellow-400' : 'text-[#493986]'}`} />
              </div>
            </div>
            <p className={`font-semibold ${theme.textPrimary}`}>My AI coach</p>
            <p className={`text-xs ${theme.textMuted}`} data-testid="widget-ai-advice-subtitle">Advice</p>
          </WidgetCard>
        ) : (
          /* Streak Widget for Athletes */
          <WidgetCard testId="widget-statistics" onClick={() => navigate("/statistics")} isDark={isDark} className="col-span-1 p-5 flex flex-col items-center justify-center">
            <div className="flex items-center gap-3 mb-3">
              <div className={`rounded-xl px-4 py-2 ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'}`}><span className={`text-2xl font-bold ${theme.textPrimary}`}>{stats.streak}</span></div>
              <div className={`rounded-xl px-4 py-2 ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'}`}><span className="text-2xl font-bold">🔥</span></div>
            </div>
            <p className={`font-semibold ${theme.textPrimary}`}>Streak</p>
            <p className={`text-xs ${theme.textMuted}`}>Keep going!</p>
          </WidgetCard>
        )}

        <WidgetCard testId="widget-sponsors" isDark={isDark} className="col-span-2 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className={`text-xs font-medium ${theme.textMuted}`}>Our Partners</p>
            <div className="flex gap-1">
              {sponsors.map((s, idx) => (
                <div key={s.id || s.name || `dot-${idx}`} className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentSponsorIndex ? 'bg-gradient-to-r from-[#493986] to-[#92a7e8] w-4' : isDark ? 'bg-white/20' : 'bg-[#493986]/20'}`} />
              ))}
            </div>
          </div>
          {(() => {
            const active = sponsors[currentSponsorIndex % sponsors.length];
            const bgColor = isDark ? 'bg-white/10' : 'bg-black/5';
            const SponsorMedia = (
              <img src={active.logo_url} alt={active.name} className={`h-8 object-contain transition-opacity duration-500`} />
            );
            return active.link_url ? (
              <a href={active.link_url} target="_blank" rel="noopener noreferrer" className={`block w-full h-16 rounded-xl ${bgColor} flex items-center justify-center transition-all duration-500 hover:scale-[1.02]`} data-testid={`sponsor-${(active.name || '').toLowerCase()}`}>
                {SponsorMedia}
              </a>
            ) : (
              <div className={`w-full h-16 rounded-xl ${bgColor} flex items-center justify-center transition-all duration-500`} data-testid={`sponsor-${(active.name || '').toLowerCase()}`}>
                {SponsorMedia}
              </div>
            );
          })()}
          <div className="flex justify-center gap-4 mt-3">
            {sponsors.map((sponsor, idx) => (
              <button
                key={sponsor.id || sponsor.name}
                type="button"
                onClick={() => {
                  setCurrentSponsorIndex(idx);
                  if (sponsor.link_url) window.open(sponsor.link_url, '_blank');
                }}
                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${idx === currentSponsorIndex ? 'bg-gradient-to-r from-[#493986] to-[#92a7e8] scale-110' : isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-[#493986]/10 hover:bg-[#493986]/20'}`}
              >
                <img src={sponsor.logo_url} alt={sponsor.name} className={`h-4 object-contain ${idx === currentSponsorIndex ? 'brightness-0 invert' : isDark ? 'brightness-0 invert opacity-60' : 'opacity-60'}`} />
              </button>
            ))}
          </div>
        </WidgetCard>
      </div>
    </div>
  );
};

export default HomePage;
